import { memo, useState } from '../../lib/teact/teact';

import type { Operator } from '../../services/crmStorage';

import {
  createOperatorApi,
  deleteOperatorApi,
  getActiveOperator,
  hashPin,
  isSuperUser,
  updateOperatorApi,
} from '../../services/crmStorage';

import useLastCallback from '../../hooks/useLastCallback';

import Icon from '../common/icons/Icon';

import styles from './OperatorsModal.module.scss';

const OPERATORS_STORAGE_KEY = 'telegram_crm_operators_v1';

const AVATAR_COLORS = [
  '#0284c7', '#7c3aed', '#059669', '#dc2626',
  '#d97706', '#db2777', '#0891b2', '#65a30d',
];

function getAvatarColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function generateId(): string {
  return `op_${Date.now().toString(36)}`;
}

type EditingState = {
  id: string | undefined;
  name: string;
  role: string;
  pin: string;
  pinConfirm: string;
};

type OwnProps = {
  isOpen: boolean;
  operators: Operator[];
  onClose: NoneToVoidFunction;
  onChange: (updated: Operator[]) => void;
};

const OperatorsModal = ({
  isOpen, operators, onClose, onChange,
}: OwnProps) => {
  const [editing, setEditing] = useState<EditingState | undefined>(undefined);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | undefined>(undefined);
  const [pinError, setPinError] = useState<string | undefined>(undefined);

  const handleOpenCreate = useLastCallback(() => {
    if (!isSuperUser(getActiveOperator())) return;
    setEditing({
      id: undefined,
      name: '',
      role: '',
      pin: '',
      pinConfirm: '',
    });
    setConfirmDeleteId(undefined);
    setPinError(undefined);
  });

  const handleOpenEdit = useLastCallback((op: Operator) => {
    if (!isSuperUser(getActiveOperator())) return;
    setEditing({
      id: op.id,
      name: op.name,
      role: op.role,
      pin: '',
      pinConfirm: '',
    });
    setConfirmDeleteId(undefined);
    setPinError(undefined);
  });

  const handleCancelEdit = useLastCallback(() => {
    setEditing(undefined);
    setPinError(undefined);
  });

  const handleSaveEdit = useLastCallback(async () => {
    if (!isSuperUser(getActiveOperator())) return;
    if (!editing) return;
    const trimmedName = editing.name.trim();
    const trimmedRole = editing.role.trim();
    if (!trimmedName) return;
    setPinError(undefined);

    let pinHash: string | undefined;
    if (editing.pin) {
      if (!/^\d{6}$/.test(editing.pin)) {
        setPinError('El PIN debe tener exactamente 6 dígitos numéricos');
        return;
      }
      if (editing.pin !== editing.pinConfirm) {
        setPinError('Los PINes no coinciden');
        return;
      }
      pinHash = await hashPin(editing.pin);
    }

    let updated: Operator[];
    if (editing.id) {
      const existingPin = operators.find((op) => op.id === editing.id)?.pin;
      updated = operators.map((op) => (op.id === editing.id
        ? {
          ...op,
          name: trimmedName,
          role: trimmedRole,
          pin: pinHash ?? existingPin,
        }
        : op));
      updateOperatorApi(editing.id, {
        name: trimmedName,
        role: trimmedRole,
        pin: editing.pin || undefined,
      }).catch(() => undefined);
    } else {
      const newOp: Operator = {
        id: generateId(),
        name: trimmedName,
        role: trimmedRole,
        pin: pinHash,
      };
      updated = [...operators, newOp];
      createOperatorApi({
        name: trimmedName,
        role: trimmedRole,
        pin: editing.pin || undefined,
      }).catch(() => undefined);
    }

    try {
      localStorage.setItem(OPERATORS_STORAGE_KEY, JSON.stringify(updated));
      window.dispatchEvent(new CustomEvent('crm:operators-changed', { detail: updated }));
    } catch {
      // Silencioso
    }

    onChange(updated);
    setEditing(undefined);
    setPinError(undefined);
  });

  const handleDeleteRequest = useLastCallback((id: string) => {
    if (!isSuperUser(getActiveOperator())) return;
    setConfirmDeleteId(id);
    setEditing(undefined);
  });

  const handleDeleteConfirm = useLastCallback(() => {
    if (!isSuperUser(getActiveOperator())) return;
    if (!confirmDeleteId) return;
    const toDeleteId = confirmDeleteId;
    const updated = operators.filter((op) => op.id !== toDeleteId);

    deleteOperatorApi(toDeleteId).catch(() => undefined);

    try {
      localStorage.setItem(OPERATORS_STORAGE_KEY, JSON.stringify(updated));
      window.dispatchEvent(new CustomEvent('crm:operators-changed', { detail: updated }));
    } catch {
      // Silencioso
    }

    onChange(updated);
    setConfirmDeleteId(undefined);
  });

  const handleDeleteCancel = useLastCallback(() => {
    setConfirmDeleteId(undefined);
  });

  const handleBackdropClick = useLastCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      setEditing(undefined);
      setConfirmDeleteId(undefined);
      onClose();
    }
  });

  if (!isOpen || !isSuperUser(getActiveOperator())) return undefined;

  const operatorToDelete = confirmDeleteId
    ? operators.find((op) => op.id === confirmDeleteId)
    : undefined;

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.dialog}>
        {/* Cabecera */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.headerIcon}>
              <Icon name="group" />
            </div>
            <div>
              <h2 className={styles.headerTitle}>Equipo Multi-Operador</h2>
              <p className={styles.headerSubtitle}>
                {operators.length}
                {' '}
                operadores · Sesión Concurrente MTProto
              </p>
            </div>
          </div>
          <button type="button" className={styles.btnClose} onClick={onClose}>
            <Icon name="close" />
          </button>
        </div>

        {/* Cuerpo */}
        <div className={styles.body}>
          {/* Lista de operadores */}
          <div className={styles.operatorsList}>
            {operators.map((op) => (
              <div key={op.id} className={styles.operatorRow}>
                <div
                  className={styles.avatar}
                  style={`background-color: ${getAvatarColor(op.id)}`}
                >
                  {op.name.charAt(0).toUpperCase()}
                </div>
                <div className={styles.operatorInfo}>
                  <div className={styles.operatorNameRow}>
                    <span className={styles.operatorName}>{op.name}</span>
                    {op.pin && (
                      <span className={styles.pinBadge} title="PIN configurado">
                        🔒 PIN
                      </span>
                    )}
                  </div>
                  <span className={styles.operatorRole}>{op.role || 'Sin rol asignado'}</span>
                </div>
                <div className={styles.operatorActions}>
                  <button
                    type="button"
                    className={styles.btnIconEdit}
                    title="Editar operador"
                    onClick={() => handleOpenEdit(op)}
                  >
                    <Icon name="edit" />
                  </button>
                  <button
                    type="button"
                    className={styles.btnIconDelete}
                    title="Eliminar operador"
                    disabled={operators.length <= 1}
                    onClick={() => handleDeleteRequest(op.id)}
                  >
                    <Icon name="delete" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Confirmación de eliminación */}
          {confirmDeleteId && operatorToDelete && (
            <div className={styles.confirmBox}>
              <p className={styles.confirmText}>
                ¿Eliminar a
                {' '}
                <strong>{operatorToDelete.name}</strong>
                {' '}
                del equipo? Esta acción no se puede deshacer.
              </p>
              <div className={styles.confirmActions}>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  onClick={handleDeleteCancel}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className={styles.btnDanger}
                  onClick={handleDeleteConfirm}
                >
                  Sí, eliminar
                </button>
              </div>
            </div>
          )}

          {/* Formulario de crear / editar */}
          {editing && (
            <div className={styles.formBox}>
              <h4 className={styles.formTitle}>
                {editing.id ? 'Editar operador' : 'Agregar nuevo operador'}
              </h4>
              <div className={styles.formFields}>
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel} htmlFor="op-name">
                    Nombre completo
                  </label>
                  <input
                    id="op-name"
                    className={styles.fieldInput}
                    type="text"
                    placeholder="Ej. María García"
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel} htmlFor="op-role">
                    Rol / Cargo
                  </label>
                  <input
                    id="op-role"
                    className={styles.fieldInput}
                    type="text"
                    placeholder="Ej. Operadora Admisiones"
                    value={editing.role}
                    onChange={(e) => setEditing({ ...editing, role: e.target.value })}
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel} htmlFor="op-pin">
                    PIN de Seguridad (6 dígitos)
                    {editing.id && (
                      <span className={styles.fieldHint}> — dejar vacío para mantener el actual</span>
                    )}
                  </label>
                  <input
                    id="op-pin"
                    className={styles.fieldInput}
                    type="password"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="••••••"
                    value={editing.pin}
                    onChange={(e) => setEditing({ ...editing, pin: e.target.value })}
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel} htmlFor="op-pin-confirm">
                    Confirmar PIN (6 dígitos)
                  </label>
                  <input
                    id="op-pin-confirm"
                    className={styles.fieldInput}
                    type="password"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="••••••"
                    value={editing.pinConfirm}
                    onChange={(e) => setEditing({ ...editing, pinConfirm: e.target.value })}
                  />
                </div>
                {pinError && <div className={styles.pinErrorMsg}>{pinError}</div>}
              </div>
              <div className={styles.formActions}>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  onClick={handleCancelEdit}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className={styles.btnPrimary}
                  disabled={!editing.name.trim()}
                  onClick={handleSaveEdit}
                >
                  <Icon name="check" />
                  <span>{editing.id ? 'Guardar cambios' : 'Agregar operador'}</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Pie */}
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.btnAdd}
            onClick={handleOpenCreate}
            disabled={Boolean(editing)}
          >
            <Icon name="add" />
            <span>Agregar Operador</span>
          </button>
          <button type="button" className={styles.btnSecondary} onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default memo(OperatorsModal);
